import { useEffect, useState, useRef, useCallback } from "react";

// Google Maps types declared in global.d.ts
import {
  googleEvent,
  emit,
  receive,
} from "../utils/dom";
import shared from "../data/js/shared";
import { useUI, useNotifications, useAppSession, useUser } from "../contexts";
import { checkName, getCountries } from "../components/utils";

import {
  FormInput,
  FormSelect,
  ErrorMessage,
} from "./single-inputs";
import { postalIsValid, regionCodesFor, regionOptionsFor } from "./guest-address-autocomplete";

import { trackApiRequest, trackApiResponse } from "../utils/track-funnel";
import { isAutofillInput } from "../utils/address-autofill";
import { trackError } from "../utils/error-tracking";
import { geocodeShippingAddress, type GeocoderLike } from "../utils/geocode-shipping-address";

const {
  nameInvalidErrorMessage,
  nameLabel,
  lastNameNeededErrorMessage,
} = shared.login;

const {
  savingLabel,
  addressInvalidErrorMessage: addressEmptyErrorMessage,
} = shared.checkout;

interface Country {
  id: number;
  status?: number;
  name: string;
  isoTwoLetter: string;
  isoThreeLetter?: string;
}

/** The surfaces this component is mounted on, each its own funnel. */
export type ShippingInputFunnel = 'selling' | 'checkout' | 'seller_address';

interface ShippingInputProps {
  /**
   * Which funnel a save from THIS mount belongs to.
   *
   * The events were hardcoded as `selling` here, and this component is mounted
   * on four surfaces: guest checkout (components/checkout-shared.tsx), the
   * account address drawer (components/address-drawer.tsx) and the sell and
   * edit-listing shipping panels (components/edit-shipping.tsx). So every
   * address a BUYER saved at checkout was written as a selling-funnel address
   * saved by somebody who had never created a listing, which is why the
   * dashboard's New Address row could read 300% of drafts saved.
   *
   * Defaults to `selling` so an un-updated caller keeps its old behaviour
   * rather than silently landing in a funnel nobody reads.
   */
  funnel?: ShippingInputFunnel;
  parentErrors?: { address?: string };
  addresses: Record<string, unknown>[];
  countries: Country[];
  getAddresses?: (profile?: Record<string, unknown>) => Promise<Record<string, unknown>[]>;
  login?: () => void;
  setDefaultAddress?: (address: Record<string, unknown>) => void;
  shipping?: Record<string, unknown>;
  setShipping?: (shipping: Record<string, unknown>) => void;
  shippingDrawerOpen?: boolean;
  setShippingDrawerOpen?: (open: boolean) => void;
  setNewAddressOpen?: (open: boolean) => void;
}

const ShippingInput: React.FC<ShippingInputProps> = ({
  funnel = 'selling' as ShippingInputFunnel,
  parentErrors,
  addresses,
  countries,
  getAddresses: _getAddresses,
  login: _login,
  setDefaultAddress: _setDefaultAddress,
  shipping: _shipping,
  setShipping: _setShipping,
  shippingDrawerOpen: _shippingDrawerOpen,
  setShippingDrawerOpen: _setShippingDrawerOpen,
  setNewAddressOpen: _setNewAddressOpen,
}) => {
  const { setTempShippingAddress, geoLocate, getAddresses: sessionGetAddresses, createAddress, tempShippingAddress } = useAppSession();
  const { setLoginOpen } = useUI();
  const { createNotification } = useNotifications();
  const { loggedIn } = useUser();

  const resetFields = {
    shippingName: tempShippingAddress.shippingName,
    line1: "",
    line2: "",
    locality: "",
    region: "",
    zipCode: "",
    countryId: "",
    latitude: "",
    longitude: "",
    isDefault: true,
  };

  const fields = {
    shippingName: tempShippingAddress.shippingName,
    line1: "",
    line2: "",
    locality: "",
    region: "",
    zipCode: "",
    countryId: "",
    latitude: "",
    longitude: "",
    isDefault: true,
  };

  const [errors, setErrors] = useState({
    ...resetFields,
    line1: parentErrors?.address,
    shippingName: ''
  });
  const [focus, setFocus] = useState({ ...fields, line1: true });
  const [filled, setFilled] = useState({ ...fields });

  const [saving, setSaving] = useState(false);
  const [isEditingAddress, setIsEditingAddressState] = useState(true);
  const [addressFocus, setAddressFocus] = useState(false);
  // Set when the text in the address box could not be turned into a full
  // street address, so the form says so instead of just not offering Save.
  const [addressNotFound, setAddressNotFound] = useState(false);
  // Street, city, state and ZIP as fields of their own, for an address the
  // lookup cannot find (a rural route, a new build). The drawer had only the
  // lookup (owner, 2026-11-03); checkout's form has had this since 10-28.
  const [manualEntry, setManualEntryState] = useState(false);
  const manualEntryRef = useRef(false);
  const setManualEntry = (on: boolean) => {
    manualEntryRef.current = on;
    setManualEntryState(on);
  };
  // USA ID is 289 in staging/dev, 237 in production
  // Canada ID is 38 in both environments
  const USA_STAGING_ID = 289;
  const USA_PROD_ID = 237;

  const [IS_USA, setIS_USA] = useState(
    !tempShippingAddress.countryId ||
      Number(tempShippingAddress.countryId) === USA_STAGING_ID ||
      Number(tempShippingAddress.countryId) === USA_PROD_ID
  );

  const acRef = useRef(null);
  // The input the Autocomplete in acRef is bound to. See initPlaces.
  const acInputRef = useRef<HTMLInputElement | null>(null);
  // The current render's placeChanged, which the one place_changed listener calls.
  const placeChangedRef = useRef<() => Promise<void>>(null);
  const inputRef = useRef(null);
  // Read by the deferred blur-commit, which fires after the render that scheduled
  // it and must see whether a suggestion has landed in the meantime.
  const tempShippingAddressRef = useRef(tempShippingAddress);
  // Whether the address box is open for typing. The blur-commit asks this, not
  // whether line1 is set: tempShippingAddress is app-wide session state and can
  // already hold some other address while this form shows an empty box, and
  // then leaving the box committed nothing (e2e on staging, 2026-10-19).
  const isEditingAddressRef = useRef(true);
  // Bumped every time the box opens or closes. A blur-commit scheduled in one
  // opening must not land in the next: picking a suggestion blurs the box, and
  // pressing ✕ within the commit delay reopened it, so the commit then typed
  // the old address back in.
  const editSessionRef = useRef(0);
  const setIsEditingAddress = (editing: boolean) => {
    editSessionRef.current += 1;
    isEditingAddressRef.current = editing;
    setIsEditingAddressState(editing);
  };
  const autofillCleanupRef = useRef<(() => void) | null>(null);

  const ref = useRef(null);

  const handleLogin = () => {
    setLoginOpen(true);
  };

  /**
   * Address fields and their minimum lengths, in the order they read on screen.
   */
  const ADDRESS_RULES: [string, number][] = [
    ["line1", 4],
    ["locality", 2],
    ["region", 2],
    ["zipCode", 4],
    ["countryId", 1],
  ];

  const saveShipping = async () => {
    if (!loggedIn) {
      handleLogin();
      return;
    }
    if (!tempShippingAddress.latitude || !tempShippingAddress.longitude) {
      const gLResponse = await geoLocate(tempShippingAddress);
      if (gLResponse) {
        // eslint-disable-next-line react-hooks/immutability -- intentional in-place persist of geolocated coords onto the existing context address; setTempShippingAddress is then called with the same reference (Object.is bail-out) so it does NOT re-render. Switching to a fresh object would re-run the [tempShippingAddress] saveShipping receive() effect and double-register the listener.
        tempShippingAddress.latitude = gLResponse.latitude;
        tempShippingAddress.longitude = gLResponse.longitude;
      }
      setTempShippingAddress(tempShippingAddress);
    }
    // Use context method instead of network.ts (uses NextAuth session, no token needed)
    // Convert string lat/long to numbers for CreateAddressRequest
    const response = await createAddress({
      ...tempShippingAddress,
      countryId: Number(tempShippingAddress.countryId),
      latitude: tempShippingAddress.latitude ? parseFloat(tempShippingAddress.latitude) : undefined,
      longitude: tempShippingAddress.longitude ? parseFloat(tempShippingAddress.longitude) : undefined,
      isDefault: tempShippingAddress.isDefault ? 1 : 0,
    });
    return response;
  };

  const sendToServer = async () => {
    const response = await saveShipping();
    return response
  };

  const checkDuplicateAddress = (
    addresses: Record<string, unknown>[],
    errors: Record<string, unknown>,
    setErrors: (errors: Record<string, unknown>) => void,
  ) => {
    // Check for duplicate based on all key address fields (same as server-side check)
    const duplicates = addresses.filter((a) => {
      const line2Match = (a.line2 || '') === (tempShippingAddress.line2 || '');
      return (
        a.line1 === tempShippingAddress.line1 &&
        line2Match &&
        a.locality === tempShippingAddress.locality &&
        a.region === tempShippingAddress.region &&
        a.zipCode === tempShippingAddress.zipCode &&
        a.countryId === tempShippingAddress.countryId
      );
    });
    if (duplicates && duplicates[0]) {
      const e = { ...errors };
      e.line1 = "duplicate";
      setErrors(e);
      googleEvent("save_address_duplicate");
      createNotification({
        type: "error",
        msg: "This address already exists in your address book",
        dismissed: false
      });
      return false;
    } else {
      const e = { ...errors };
      e.line1 = "valid";
      setErrors(e);
    }
    return true;
  };

  const gotoError = () => {
    setTimeout(() => {
      const em = ref?.current?.querySelectorAll(".invalid")[0];
      if (em && em.scrollIntoView) {
        em.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        // em.querySelector(".form-input, .form-select").focus();
      }
    }, 100);
  };

  // Reached from Save, which only renders once a street is in, or from the
  // saveShipping event after checkAll passed -- so line1 is always set here.
  // A parent's "address empty" check used to run first, and could not fire.
  const submitShipping = async () => {
    if (
      checkAll() &&
      checkDuplicateAddress(addresses, errors, setErrors as (e: Record<string, unknown>) => void)
    ) {
      setSaving(true);
      // Track save_address API request
      trackApiRequest(funnel, 'save_address', {
        locality: tempShippingAddress.locality,
        region: tempShippingAddress.region,
        has_line1: !!tempShippingAddress.line1,
      });
      const response = await sendToServer();
      setSaving(false);
      if (!response) {
        trackApiResponse(funnel, 'save_address', false, { error: 'No response' });
        return;
      }
      if (response?.success) {
        trackApiResponse(funnel, 'save_address', true, {
          address_id: response.address.id,
        });
        createNotification({ type: "success", msg: "Saved Address", dismissed: false });
        await sessionGetAddresses();
        setErrors(resetFields);
        setTempShippingAddress(fields);
        setManualEntry(false);
        setIsEditingAddress(true);
        googleEvent("save_address_success", tempShippingAddress);
        setTimeout(() => {
          emit("afterAddressAdded");
        }, 100);
      } else {
        // createAddress answers a failure as { success: false, error }.
        const { error } = response as { error?: string };
        trackApiResponse(funnel, 'save_address', false, { error });
        googleEvent("save_address_error");
        createNotification({ type: "error", msg: error || "Error Sending to Server", dismissed: false });
      }
    } else {
      gotoError();
    }
  };

  const checkAll = () => {
    // One accumulating error object for the whole form. Each check used to spread
    // the same stale `errors` and call setErrors itself, so only the last write
    // survived and every earlier field's result was discarded; and the two halves
    // were joined with `&&`, so a bad address hid a bad name entirely.
    const acc: typeof errors = { ...errors };
    const bag = acc as unknown as Record<string, unknown>;
    // `.map(...).every(...)`, not a chain of `&&`: short-circuiting stopped at the
    // first bad field, so a form with two problems reported them one click apart.
    const lengthsValid = ADDRESS_RULES.map(([id, min]) => checkInto(bag, id, min)).every(Boolean);
    // Typed by hand, the state and ZIP are checked for real; the lookup only
    // ever hands over a real state code and ZIP.
    const shapeValid = !manualEntryRef.current || checkManualShape(bag);
    const addressValid = lengthsValid && shapeValid;
    const nameValid = checkName(
      tempShippingAddress,
      bag,
      (next: Record<string, unknown>) => Object.assign(bag, next),
      "shippingName",
    );
    setErrors(acc);
    return addressValid && nameValid;
  };

  /**
   * Apply one field's minimum-length rule, recording the result in `acc`.
   *
   * The length test used to read `(value + "".trim()).length`, which trims the
   * empty string and concatenates it -- so the field itself was never trimmed and
   * four spaces passed as a street address.
   */
  const checkInto = (acc: Record<string, unknown>, id: string, min: number) => {
    const value = `${tempShippingAddress[id] ?? ""}`.trim();
    if (value.length < min) {
      acc[id] = "short";
      return false;
    }
    acc[id] = "valid";
    return true;
  };

  /** The two-letter code of the chosen country, from the drawer's country list. */
  const isoOf = (countryId: unknown) =>
    getCountries().find((c) => String(c.id) === String(countryId ?? ""))?.isoTwoLetter ?? "US";

  /** A typed state must be one of the country's, and a ZIP or postal code must look like one. */
  const checkManualShape = (acc: Record<string, unknown>) => {
    const iso = isoOf(tempShippingAddress.countryId);
    const regionOk = regionCodesFor(iso).includes(`${tempShippingAddress.region ?? ""}`);
    const zipOk = postalIsValid(iso, `${tempShippingAddress.zipCode ?? ""}`.trim());
    if (!regionOk) acc.region = "short";
    if (!zipOk) acc.zipCode = "short";
    return regionOk && zipOk;
  };

  /** Open the fields: what was typed in the lookup starts the street line, in the US by default. */
  const openManualEntry = () => {
    const typed = ((inputRef.current as HTMLInputElement | null)?.value || "").split(",")[0].trim();
    setTempShippingAddress({
      ...tempShippingAddress,
      line1: tempShippingAddress.line1 || typed,
      countryId: tempShippingAddress.countryId || String(getCountries()[0].id),
    });
    setAddressNotFound(false);
    setManualEntry(true);
    googleEvent("address_manual_opened");
  };

  /** Back to the lookup. What was typed in the fields is kept, nothing is saved. */
  const closeManualEntry = () => {
    setManualEntry(false);
    setErrors(resetFields);
    setIsEditingAddress(true);
  };

  const handleEditAddress = () => {
    setIsEditingAddress(true);
    setAddressFocus(true);
    // Keep the apt line as well as the name. Coming back to correct the street
    // used to discard it, and unlike the name there was nothing to retype it
    // from -- the field itself is unmounted while the address is being edited.
    setTempShippingAddress({
      ...resetFields,
      shippingName: tempShippingAddress.shippingName,
      line2: tempShippingAddress.line2,
    });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const buildAddyFromComponents = (
    address_components: Array<{ types: string[]; long_name: string; short_name: string }>,
    geometry: { location: { lat: () => number; lng: () => number } } | undefined,
  ) => {
    // line2 rides along with shippingName: it is the buyer's own text, and Google
    // returns no subpremise here, so writing "" would only blank an apt line they
    // had already typed. Read through the ref: the keyboard and blur handlers
    // that call this were made on an earlier render.
    const { shippingName, line2 } = tempShippingAddressRef.current;
    const addy: Record<string, unknown> = {
      line1: "",
      line2,
      locality: "",
      region: "",
      zipCode: "",
      latitude: "",
      longitude: "",
      countryId: "",
      country: "",
      shippingName,
    };
    if (geometry?.location) {
      addy.latitude = geometry.location.lat();
      addy.longitude = geometry.location.lng();
    }
    const countriesArray = countries && countries.length > 0 ? countries : getCountries();
    for (const component of address_components) {
      const componentType = component.types[0];
      switch (componentType) {
        case "street_number":
          addy.line1 = `${component.long_name} ${addy.line1}`;
          break;
        case "route":
          addy.line1 += component.short_name;
          break;
        case "postal_code":
          addy.zipCode = `${component.long_name}${addy.zipCode}`;
          break;
        case "postal_code_suffix":
          addy.zipCode = `${addy.zipCode}-${component.long_name}`;
          break;
        case "locality":
          addy.locality = component.long_name;
          break;
        case "administrative_area_level_1":
          addy.region = component.short_name;
          break;
        case "country": {
          // countriesArray is never empty: getCountries() is a fixed US/Canada list.
          const c =
            countriesArray.filter((c) => c.isoTwoLetter == component.short_name)[0] ||
            countriesArray[0];
          addy.countryId = c.id;
          addy.country = component.long_name;
          break;
        }
      }
    }
    if (!addy.countryId && countriesArray.length > 0) {
      addy.countryId = countriesArray[0].id;
    }
    addy.line1 = (addy.line1 as string).trim();
    return addy;
  };

  /**
   * Geocode what is in the address box. `report` is for a commit -- a picked
   * suggestion, Enter, leaving the box -- where a miss has to be said out loud;
   * the autofill check runs mid-typing, where a miss is expected.
   */
  const parseAddressFromText = async (addressText: string, report = false) => {
    if (!addressText || !addressText.trim()) return;
    if (typeof window.google === "undefined" || !window.google?.maps) return;
    const notFound = () => {
      if (!report) return;
      setAddressNotFound(true);
      googleEvent("address_not_found");
    };
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const geocoder: GeocoderLike = new (window.google as any).maps.Geocoder();
      const place = await geocodeShippingAddress(geocoder, addressText);
      if (!place) return notFound();
      const addy = buildAddyFromComponents(place.address_components, place.geometry);
      if (addy.line1 && addy.locality && addy.region && addy.zipCode) {
        setTempShippingAddress({ ...addy, isDefault: false } as typeof tempShippingAddress);
        setErrors(resetFields);
        setIsEditingAddress(false);
        setAddressNotFound(false);
      } else {
        notFound();
      }
    } catch (error) {
      // The Geocoder rejects text it cannot place with ZERO_RESULTS: the buyer
      // is told on the field and can keep typing. Anything else (quota, a
      // refused key) is ours to fix.
      const code = (error as { code?: string } | null)?.code;
      if (code !== "ZERO_RESULTS") {
        trackError(error instanceof Error ? error : new Error(String(error)), undefined, {
          component: "ShippingInput",
          action: "geocode_address",
          extra: { code },
        });
      }
      notFound();
    }
  };

  const placeChanged = async () => {
    const place = acRef.current.getPlace();
    if (!place || !place.address_components) {
      // Google can suggest a place its own details lookup no longer finds: on
      // 2026-10-01 "600 Congress Ave., Austin" came back NOT_FOUND, so picking
      // it set nothing and the Save button never appeared. The suggestion's
      // text is in the box by now, and the Geocoder still resolves it.
      void parseAddressFromText((inputRef.current as HTMLInputElement | null)?.value || "", true);
      return;
    }
    const addy = buildAddyFromComponents(place.address_components, place.geometry);
    setTempShippingAddress({ ...addy, isDefault: false } as typeof tempShippingAddress);
    setErrors(resetFields);
    setIsEditingAddress(false);
    setAddressNotFound(false);
  };

  const MIN_AUTOFILL_LENGTH = 10;
  const AUTOFILL_DETECTION_DELAY_MS = 100;
  // Clicking a Google suggestion blurs the input before `place_changed` fires,
  // so the blur-commit waits for that to land before deciding the buyer left
  // without picking anything.
  const BLUR_COMMIT_DELAY_MS = 350;

  /**
   * Commit whatever is typed in the address box when the buyer leaves it.
   *
   * Only Enter, clicking a suggestion, or the autofill heuristic above committed
   * an address. Someone who typed theirs and moved on to another field left
   * `line1` empty -- and with it empty, neither the apt line nor the Save button
   * is rendered at all, so the form simply stopped, with nothing on screen
   * saying why.
   */
  const handleAddressBlur = () => {
    setAddressFocus(false);
    const typed = (inputRef.current as HTMLInputElement | null)?.value || "";
    if (!typed.trim() || !isEditingAddressRef.current) return;
    const session = editSessionRef.current;

    setTimeout(() => {
      // A suggestion that landed in the meantime closed the box, and the box
      // may have been reopened since: either way this text is no longer what
      // is being edited.
      if (editSessionRef.current !== session || !isEditingAddressRef.current) return;
      void parseAddressFromText(typed, true);
    }, BLUR_COMMIT_DELAY_MS);
  };

  const initPlaces = () => {
    if (typeof window.google != "undefined" && window.google?.maps?.places && inputRef.current) {
      const inputEl = inputRef.current;

      // One Autocomplete per input. This effect re-runs on every keystroke in
      // the name box, and each run used to bind ANOTHER Autocomplete to the same
      // input, each with a place_changed listener closed over the name as it was
      // then. Picking a suggestion let a stale one win and wrote a half-typed
      // name back ("Sam Riv" for "Sam Rivera"), which then saved.
      if (acInputRef.current !== inputEl) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        acRef.current = new (window.google as any).maps.places.Autocomplete(inputEl, {
          fields: ["address_components", "geometry"],
          types: ["address"],
          componentRestrictions: { country: ["us", "ca"] },
        });
        acRef.current.addListener("place_changed", () => void placeChangedRef.current());
        acInputRef.current = inputEl;
      }

      // Browser autofill / Enter-without-selecting fallback: geocode the typed
      // text so place_changed isn't the only path into the next view.
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          const place = acRef.current?.getPlace();
          if (place && place.address_components) {
            void placeChangedRef.current();
          } else {
            void parseAddressFromText((e.target as HTMLInputElement).value, true);
          }
        }
      };
      // Autofill and paste put a whole address in with one input event; typing
      // adds a character at a time. Checking on every keystroke looked up the
      // half-typed street (the guest checkout field had the same flaw). Phone
      // keyboards add a whole suggested word at once, so isAutofillInput also
      // reads the inputType and wants more than one word.
      let lastValue = inputEl.value;
      const handleInput = (e: Event) => {
        const target = e.target as HTMLInputElement;
        // They are changing it, so the last miss no longer describes the box.
        setAddressNotFound(false);
        const prev = lastValue;
        lastValue = target.value;
        if (!isAutofillInput(e, prev, target.value)) return;
        setTimeout(() => {
          const value = target.value;
          if (
            value &&
            value.length > MIN_AUTOFILL_LENGTH &&
            !tempShippingAddressRef.current.line1
          ) {
            void parseAddressFromText(value);
          }
        }, AUTOFILL_DETECTION_DELAY_MS);
      };
      inputEl.addEventListener("keydown", handleKeyDown);
      inputEl.addEventListener("input", handleInput);
      autofillCleanupRef.current = () => {
        inputEl.removeEventListener("keydown", handleKeyDown);
        inputEl.removeEventListener("input", handleInput);
      };
    } else {
      setTimeout(() => {
        initPlaces();
      }, 1000);
    }
  };

  useEffect(() => {
    initPlaces();
    return () => {
      if (autofillCleanupRef.current) {
        autofillCleanupRef.current();
        autofillCleanupRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempShippingAddress.shippingName, isEditingAddress]);

  useEffect(() => {
    // Check if countryId is USA (both staging and production IDs)
    const isUSA = !tempShippingAddress.countryId ||
        Number(tempShippingAddress.countryId) === USA_STAGING_ID ||
        Number(tempShippingAddress.countryId) === USA_PROD_ID;

    // If country changed and region is set, clear it to avoid invalid state/province codes
    if (IS_USA !== isUSA && tempShippingAddress.region) {
      setTempShippingAddress({...tempShippingAddress, region: ''});
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clears the stale region validation error only when the country toggles USA<->non-USA; this is event-driven synchronization keyed off a countryId change (prev IS_USA vs new isUSA), not derivable during render.
      setErrors({...errors, region: ''});
    }

    setIS_USA(isUSA);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempShippingAddress.countryId]);

  useEffect(() => {
    tempShippingAddressRef.current = tempShippingAddress;
    isEditingAddressRef.current = isEditingAddress;
    placeChangedRef.current = placeChanged;
  });

  const submitForm = () => {
    if (!saving) void submitShipping();
  };

  /*
   * One "saveShipping" listener for the life of the form, calling this render's
   * handler. `receive` removes a listener by reference, and this used to hand it
   * a fresh closure on every change to the address -- so nothing was ever
   * removed, every keystroke added another listener, and one Save on the sell
   * page or at checkout ran a submit per stale closure: errors from long-gone
   * values written over the form, and the address posted more than once.
   */
  const onSaveShippingRef = useRef<() => void>(null);
  useEffect(() => {
    onSaveShippingRef.current = () => {
      if (checkAll()) submitForm();
    };
  });
  useEffect(() => {
    const onSaveShipping = () => onSaveShippingRef.current();
    receive("saveShipping", onSaveShipping);
    return () => document.removeEventListener("saveShipping", onSaveShipping);
  }, []);

  const validateShippingName = useCallback(() => {
    return checkName(tempShippingAddress, errors, setErrors, "shippingName");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempShippingAddress.shippingName]);

  return (
    <div className="simple-address-input max-w-full px-2" ref={ref} data-testid="shipping-input-panel">
      {/* Name Input */}
      <div className="mb-1 w-full" data-testid="shipping-name-input-container">
        <FormInput
          id="shippingName"
          onFocus={setFocus}
          min=""
          autocomplete="shipping name"
          setState={setTempShippingAddress}
          label={nameLabel}
          validate={validateShippingName}
          state={tempShippingAddress}
          errors={errors}
          focus={focus}
          filled={filled}
          setFilled={setFilled}
          setErrors={setErrors}
          errorMessages={
            <>
              <ErrorMessage
                cls="name-invalid-error"
                msg={nameInvalidErrorMessage}
              />
              <ErrorMessage
                cls="lastName-needed-error"
                msg={lastNameNeededErrorMessage}
              />
            </>
          }
        />
      </div>

      {/* Address Autocomplete or Selected Address */}
      {!manualEntry && (
      <div className="mb-1 w-full">
        <label
          className={`input-label input-label-address w-full ${addressFocus ? 'focus' : ''} ${tempShippingAddress.line1 ? 'filled' : ''}`}
          htmlFor="address-input"
        >
          {isEditingAddress ? (
            <input
              ref={inputRef}
              type="text"
              id="address-input"
              data-testid="address-autocomplete-input"
              className="form-input bordered google-autocomplete w-full box-border"
              placeholder="Start typing your address..."
              autoComplete="off"
              onFocus={() => setAddressFocus(true)}
              onBlur={handleAddressBlur}
            />
          ) : (
            <div className="address defaultAddress" data-testid="selected-address-display">
              <div className="text">
                <span className="highlight">
                  {tempShippingAddress.line1}
                  <br />
                  {tempShippingAddress.line2 && (
                    <>
                      {tempShippingAddress.line2}
                      <br />
                    </>
                  )}
                  {tempShippingAddress.locality}, {tempShippingAddress.region}, {tempShippingAddress.zipCode}
                </span>
              </div>
              <div
                className="delete-button cursor-pointer"
                data-testid="edit-address-button"
                onClick={handleEditAddress}
              >
                {"✕"}
              </div>
            </div>
          )}
          {/* The SCSS has styled `.address-empty-error` under STATE--line1-empty
              and -short since forever, but nothing ever rendered one -- so a
              failed address check styled the field red and said nothing. */}
          <ErrorMessage
            cls="address-empty-error"
            msg={addressEmptyErrorMessage}
          />
          <div className="valid-check" />
        </label>
        {isEditingAddress && addressNotFound && (
          <p role="alert" data-testid="address-not-found" className="mt-1 text-sm text-red-600 dark:text-red-400">
            We could not find that address. Pick a suggestion with a street number, or type the full address with
            its city and ZIP.
          </p>
        )}
        {isEditingAddress && (
          <button
            type="button"
            className="guest-address-manual-link highlight"
            data-testid="shipping-enter-address-manually"
            onClick={openManualEntry}
          >
            Can&apos;t find it? Enter it manually
          </button>
        )}
      </div>
      )}

      {manualEntry && (
        <div className="guest-address-manual w-full" data-testid="shipping-address-manual">
          <div className="guest-address-manual-head">
            <p className="guest-address-manual-note">Enter your address</p>
            <button
              type="button"
              className="guest-address-manual-link highlight"
              data-testid="shipping-close-manual-entry"
              onClick={closeManualEntry}
            >
              Use address lookup
            </button>
          </div>
          {([
            ["line1", "Street address", "shipping address-line1", "Enter your street address"],
            ["locality", "City", "shipping address-level2", "Enter your city"],
            ["line2", "Apt, suite, unit, c/o (optional)", "shipping address-line2", ""],
          ] as const).map(([id, label, autocomplete, message]) => (
            <div className="mb-1" key={id}>
              <FormInput
                id={id}
                onFocus={setFocus}
                min=""
                autocomplete={autocomplete}
                setState={setTempShippingAddress}
                label={label}
                state={tempShippingAddress}
                errors={errors}
                focus={focus}
                filled={filled}
                setFilled={setFilled}
                setErrors={setErrors}
                validate={undefined}
                errorMessages={message ? <ErrorMessage cls={`${id}-error`} msg={message} /> : null}
              />
            </div>
          ))}
          <div className="guest-address-manual-row">
            <div className="mb-1">
              <FormSelect
                id="countryId"
                autocomplete="shipping country"
                options={getCountries().map((c) => ({ id: String(c.id), name: c.name }))}
                onFocus={setFocus}
                setState={setTempShippingAddress}
                label="Country"
                state={tempShippingAddress}
                errors={errors}
                focus={focus}
                filled={filled}
                setFilled={setFilled}
                setErrors={setErrors}
                validate={undefined}
                onBlur={undefined}
                onChange={undefined}
                realName={undefined}
                errorMessages={<ErrorMessage cls="countryId-error" msg="Choose your country" />}
              />
            </div>
            <div className="mb-1">
              <FormSelect
                id="region"
                autocomplete="shipping address-level1"
                options={regionOptionsFor(isoOf(tempShippingAddress.countryId))}
                onFocus={setFocus}
                setState={setTempShippingAddress}
                label={isoOf(tempShippingAddress.countryId) === "CA" ? "Province" : "State"}
                state={tempShippingAddress}
                errors={errors}
                focus={focus}
                filled={filled}
                setFilled={setFilled}
                setErrors={setErrors}
                validate={undefined}
                onBlur={undefined}
                onChange={undefined}
                realName={undefined}
                errorMessages={<ErrorMessage cls="region-error" msg={isoOf(tempShippingAddress.countryId) === "CA" ? "Choose your province" : "Choose your state"} />}
              />
            </div>
            <div className="mb-1">
              <FormInput
                id="zipCode"
                onFocus={setFocus}
                min=""
                inputMode={isoOf(tempShippingAddress.countryId) === "CA" ? "text" : "numeric"}
                autocomplete="shipping postal-code"
                setState={setTempShippingAddress}
                label={isoOf(tempShippingAddress.countryId) === "CA" ? "Postal code" : "ZIP code"}
                state={tempShippingAddress}
                errors={errors}
                focus={focus}
                filled={filled}
                setFilled={setFilled}
                setErrors={setErrors}
                validate={undefined}
                errorMessages={<ErrorMessage cls="zipCode-error" msg={isoOf(tempShippingAddress.countryId) === "CA" ? "Enter a postal code like A1A 1A1" : "Enter a 5-digit ZIP code"} />}
              />
            </div>
          </div>
          <div className="button-cont same-color mt-2 w-full">
            <button
              type="button"
              data-testid="save-address-button"
              className="greenButton save-address-button w-full max-w-xs mx-auto block"
              onClick={submitForm}
              disabled={saving}
            >
              {saving ? savingLabel : 'Save address'}
            </button>
          </div>
        </div>
      )}

      {/* Apt / Suite / c/o input + explicit save — shown after a base address is selected */}
      {!isEditingAddress && tempShippingAddress.line1 && (
        <>
          <div className="mb-1 w-full">
            <FormInput
              id="line2"
              onFocus={setFocus}
              min=""
              autocomplete="address-line2"
              setState={setTempShippingAddress}
              label="Apt, suite, unit, c/o (optional)"
              state={tempShippingAddress}
              errors={errors}
              focus={focus}
              filled={filled}
              setFilled={setFilled}
              setErrors={setErrors}
              validate={undefined}
              errorMessages={null}
            />
          </div>
          <div className="button-cont same-color mt-2 w-full">
            <button
              type="button"
              data-testid="save-address-button"
              className="greenButton save-address-button w-full max-w-xs mx-auto block"
              onClick={submitForm}
              disabled={saving}
            >
              {saving ? savingLabel : 'Save address'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ShippingInput;
